import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../config/environment';
import { api, authApi } from '../api/client';

const BACKEND_URL = config.backendUrl;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start true to prevent flash
  const [tenantId, setTenantId] = useState(null);
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [isChecking, setIsChecking] = useState(false);

  const AUTH_CHECK_TIMEOUT = 5000; // 5 seconds

  // PASSWORD-ONLY LOGIN - Hardcoded password: Zuperbook123
  const CORRECT_PASSWORD = 'Zuperbook123';

  // ============================================================
  // SESSION RESTORATION ON PAGE LOAD
  // ============================================================
  useEffect(() => {
    const restoreSession = () => {
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      const storedTenantId = localStorage.getItem('tenant_id');
      const storedEmail = localStorage.getItem('user_email');

      if (isAuthenticated && storedTenantId) {
        // Restore user session from localStorage
        const restoredUser = {
          user_id: 'dev-user-123',
          email: storedEmail || 'nishan@exptek.com',
          name: 'Nishan',
          tenant_id: storedTenantId,
          role: 'Owner',
          company_name: 'ZuperBooks Dev'
        };

        const fullPermissions = {
          products: 'full',
          inventory: 'full',
          finance: 'full',
          purchase_orders: 'full',
          pricing: 'full',
          sales: 'full',
          insights: 'full',
          reports: 'full',
          dashboard: 'full',
          settings: 'full',
          users: 'full',
          billing: 'full'
        };

        setUser(restoredUser);
        setTenantId(storedTenantId);
        setRole('Owner');
        setPermissions(fullPermissions);
        setTenants([{ tenant_id: storedTenantId, company_name: 'ZuperBooks Dev' }]);

        console.log('✅ Session restored from localStorage:', {
          email: restoredUser.email,
          tenant_id: storedTenantId
        });
      } else {
        console.log('ℹ️ No stored session found');
      }

      setLoading(false);
    };

    restoreSession();
  }, []);

  const login = async (password) => {
    if (password !== CORRECT_PASSWORD) {
      throw new Error('Incorrect password');
    }

    // Hardcoded development user
    const devUser = {
      user_id: 'dev-user-123',
      email: 'nishan@exptek.com',
      name: 'Nishan',
      tenant_id: 'tenant-123',
      role: 'Owner',
      company_name: 'ZuperBooks Dev'
    };

    // Full permissions for all modules
    const fullPermissions = {
      products: 'full',
      inventory: 'full',
      finance: 'full',
      purchase_orders: 'full',
      pricing: 'full',
      sales: 'full',
      insights: 'full',
      reports: 'full',
      dashboard: 'full',
      settings: 'full',
      users: 'full',
      billing: 'full'
    };

    // Set user state
    setUser(devUser);
    setTenantId(devUser.tenant_id);
    setRole(devUser.role);
    setPermissions(fullPermissions);
    setTenants([{ tenant_id: devUser.tenant_id, company_name: devUser.company_name }]);

    // Store in localStorage for persistence
    localStorage.setItem('tenant_id', devUser.tenant_id);
    localStorage.setItem('user_email', devUser.email);
    localStorage.setItem('isAuthenticated', 'true');

    console.log('✅ Login successful:', {
      email: devUser.email,
      tenant_id: devUser.tenant_id,
      role: devUser.role
    });

    return true;
  };

  const checkAuth = async () => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    return isAuthenticated;
  };

  const logout = async () => {
    try {
      setUser(null);
      setTenantId(null);
      setRole(null);
      setPermissions(null);
      setTenants([]);
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('user_email');
      console.log('✅ Logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  const switchTenant = async (newTenantId) => {
    try {
      // Use centralized API client
      const data = await api.post(`/api/v1/auth/switch-tenant/${newTenantId}`, {});

      setTenantId(newTenantId);
      setRole(data.role);
      setUser(prev => ({ ...prev, company_name: data.company_name }));

      console.log('✅ Switched to tenant:', newTenantId);
      
      // Reload the page to refresh data for new tenant
      window.location.reload();
    } catch (error) {
      console.error('❌ Switch tenant error:', error);
      throw error;
    }
  };

  const value = {
    user,
    tenantId,
    role,
    permissions,
    tenants,
    loading,
    isAuthenticated: !!user,
    checkAuth,
    login,
    logout,
    switchTenant
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
